import { useState } from 'react';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';
import { isPermission } from '@hp/domain';
import { IconButton } from '@hp/ui';
import { domainErrorOf, errText, requestIdOf } from '../lib/format';

/** A domain error's machine code and details, as `domainErrorOf` reads them from a tRPC/REST error. */
export type DomainErrorInfo = NonNullable<ReturnType<typeof domainErrorOf>>;

/**
 * Inline error block; renders nothing when there is no error. Known domain codes are worded in
 * the UI language, everything else shows the server's message verbatim. Dismissable — and a
 * NEW error replaces a dismissed one (dismissal resets whenever the error object changes).
 * A feature words its own machine codes through `describe` (already translated text, or
 * undefined to fall back to the shared wording).
 */
export function ErrorAlert({
  error,
  className,
  describe,
}: {
  error: unknown;
  className?: string;
  describe?: (domain: DomainErrorInfo) => string | undefined;
}) {
  const { t } = useTranslation();
  // Dismissal is tied to the error object itself, so a new error shows again without an effect.
  const [dismissedError, setDismissedError] = useState<unknown>(null);

  if (!error || dismissedError === error) return null;
  const ref = requestIdOf(error);
  return (
    <div className={className ? `bo-alert ${className}` : 'bo-alert'} role="alert">
      <span>
        {describeError(error, t, describe)}
        {ref && (
          <small className="bo-alert__ref" title={t('errors.requestIdTitle')}>
            {t('errors.ref', { id: ref })}
          </small>
        )}
      </span>
      <IconButton
        icon="close"
        aria-label={t('actions.dismiss')}
        title={t('actions.dismiss')}
        onClick={() => setDismissedError(error)}
      />
    </div>
  );
}

/** The API's messages are English; the codes it attaches let us word the common ones locally. */
function describeError(
  error: unknown,
  t: TFunction<'common'>,
  describe?: (domain: DomainErrorInfo) => string | undefined,
): string {
  const domain = domainErrorOf(error);
  const own = domain && describe ? describe(domain) : undefined;
  if (own !== undefined) return own;
  const permission = domain?.details?.permission;
  if (domain?.code === 'PERMISSION_REQUIRED' && isPermission(permission)) {
    return t('errors.permissionRequired', { permission: t(`permission.${permission}`) });
  }
  return errText(error);
}
